var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box } from '@mui/material';
import React from 'react';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router';
import { GlobalError } from '../GlobalError/GlobalError';
import { NavigationContextProvider } from '../NavigationContext';
import { GlobalAppRootElement, mapGlobalAppRouteObject, tabsPath } from './components';
import { GlobalAppContext } from './GlobalAppContext';
/**
 * The root component of the global application, setting up routing, navigation context,
 * and global state management. It integrates top-level and bottom menu routes and provides
 * a centralized global app context API.
 *
 * @param {GlobalAppProps} props - Props for the GlobalApp component.
 * @param {GlobalAppRouteObject[]} props.routes - Top-level application routes.
 * @param {GlobalAppRouteObject[]} [props.bottomMenuRoutes] - Optional bottom menu route configurations.
 * @param {(element: React.ReactElement) => React.ReactNode} [props.rootElement] - Optional function to wrap the root route element.
 * @param {string} [props.basename] - Optional base path for routing.
 * @param {React.Ref<GlobalAppContextType>} [props.ref ] - Ref to expose the GlobalAppContextType API to parent components.
 *
 * @returns {JSX.Element} The initialized global application with routing and context providers.
 */
export const GlobalApp = (_a) => {
    var _b;
    var { ref: forwardedRef } = _a, props = __rest(_a, ["ref"]);
    const ref = React.useRef(null);
    const globalContextValue = React.useRef({
        setProfileBadge: (badge) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setProfileBadge(badge);
        },
        setMenuItemBadge: (path, badge) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setMenuItemBadge(path, badge);
        },
        setInfoProps: (props) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setInfoProps(props);
        },
        getTabs: () => {
            var _a, _b;
            return (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getTabs()) !== null && _b !== void 0 ? _b : [];
        },
        setTabs: (tabs, selectedTabPath) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setTabs(tabs, selectedTabPath);
        },
        openExistingTab: (tabPath) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.openExistingTab(tabPath);
        },
        openTab: (tab) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.openTab(tab);
        },
        closeTab: (tabPath) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.closeTab(tabPath);
        },
        setTabBadge: (tabPath, badge) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setTabBadge(tabPath, badge);
        },
        setDrawerOpen: (open, props) => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setDrawerOpen(open, props);
        },
        toggleDrawerOpen: () => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.toggleDrawerOpen();
        },
        navigate: ((to, options) => {
            var _a;
            return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.navigate(to, options);
        }),
    });
    // Expose the globalContextValue to the parent via forwardedRef
    React.useImperativeHandle(forwardedRef, () => globalContextValue.current, [globalContextValue]);
    const children = createRootRouteChildren(props.routes, (_b = props.bottomMenuRoutes) !== null && _b !== void 0 ? _b : [], props.basename);
    const rootElement = React.createElement(GlobalAppRootElement, Object.assign({}, props, { ref: ref }));
    const router = createBrowserRouter([
        {
            path: '/',
            element: props.rootElement ? props.rootElement(rootElement) : rootElement,
            children: [
                ...children,
                {
                    path: '*',
                    element: React.createElement(GlobalError, { type: 'not-found' }),
                    errorElement: React.createElement(GlobalError, null),
                },
            ],
            errorElement: React.createElement(GlobalError, null),
        },
    ], { basename: props.basename });
    return (React.createElement(NavigationContextProvider, null,
        React.createElement(GlobalAppContext.Provider, { value: globalContextValue.current },
            React.createElement(RouterProvider, { router: router }))));
};
/**
 * Creates the root route configuration by combining top-level and bottom menu routes,
 * appending fallback and tab-specific routing as needed.
 *
 * @param {GlobalAppRouteObject[]} routes - The main application routes.
 * @param {GlobalAppRouteObject[]} bottomMenuRoutes - Additional routes shown in the bottom menu.
 * @param {string} [basename] - Optional base path for routing.
 * @returns {RouteObject[]} An array of route objects suitable for React Router.
 */
function createRootRouteChildren(routes, bottomMenuRoutes, basename) {
    const children = [...routes, ...bottomMenuRoutes].map((route) => mapGlobalAppRouteObject(route, basename !== null && basename !== void 0 ? basename : '/'));
    children.push({
        path: `${tabsPath}`,
        element: React.createElement(SelectGlobalTabByPath, null),
        children: [
            {
                path: '*',
                element: React.createElement(SelectGlobalTabByPath, null),
            },
        ],
    });
    if (children.findIndex((route) => route.path === '*') === -1) {
        children.push({
            path: '*',
            element: React.createElement(GlobalError, { type: 'not-found' }),
        });
    }
    return children;
}
const SelectGlobalTabByPath = () => {
    const location = useLocation();
    React.useEffect(() => {
        const mainContentTabs = document.querySelectorAll('.main-content-tab');
        let tabFound = false;
        const tryingToAccessTabs = location.pathname.startsWith(`/${tabsPath}/`);
        if (tryingToAccessTabs) {
            const currentTabPath = location.pathname.substring(tabsPath.length + 2);
            for (const mainContentTab of mainContentTabs) {
                const tabPath = mainContentTab.getAttribute('data-tab-path');
                if (currentTabPath === tabPath || currentTabPath.startsWith(tabPath + '/')) {
                    mainContentTab.style.display = 'block';
                    tabFound = true;
                }
                else {
                    mainContentTab.style.display = 'none';
                }
            }
        }
        const mainContentWrapper = document.getElementById('main-content-wrapper');
        if (mainContentWrapper) {
            mainContentWrapper.style.display = tryingToAccessTabs && !tabFound ? 'block' : 'none';
        }
        return () => {
            for (const mainContentTab of mainContentTabs) {
                mainContentTab.style.display = 'none';
            }
            if (mainContentWrapper) {
                mainContentWrapper.style.display = 'block';
            }
        };
    }, [location.pathname]);
    return React.createElement(Box, null, "TAB NOT FOUND");
};
